import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  Wrench, Plus, Search, ChevronRight, RefreshCw, Phone, MapPin,
  StickyNote, Download, Trash2, CheckSquare, Loader2, X, Clock,
  CheckCircle, Calendar, Filter, Truck, Printer, ChevronDown
} from "lucide-react";
import { StatusBadge, LEGACY_PIPELINE, getStep } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN",  { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

const toISO   = d => d.toISOString().slice(0, 10);     // YYYY-MM-DD
const today   = () => toISO(new Date());
const offsetDay = n => { const d = new Date(); d.setDate(d.getDate() + n); return toISO(d); };

const MONTHS_LABELS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// Generate last 18 months for month filter
const genMonthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ label: `${MONTHS_LABELS[d.getMonth()]} ${d.getFullYear()}`, month: d.getMonth() + 1, year: d.getFullYear() });
  }
  return opts;
};
const MONTH_OPTIONS = genMonthOptions();

// ── Design helpers ─────────────────────────────────────────────────────────────
const WARRANTY_COLORS = {
  "Under Warranty":  "bg-green-50 text-green-700 border-green-200",
  "Out of Warranty": "bg-amber-50 text-amber-700 border-amber-200",
  "Expired":         "bg-rose-50 text-rose-700 border-rose-200",
  "XP Charger":      "bg-blue-50 text-blue-700 border-blue-200",
  "Unknown":         "bg-slate-100 text-slate-500 border-slate-200",
};
const PAY_STATUS_COLORS = { "Paid":"text-emerald-700", "Partial":"text-amber-600", "Unpaid":"text-rose-600" };
const CHARGER_ICONS = { "Small Charger":"🔋", "Fast Charger":"⚡", "Rickshaw Charger":"🛺" };

const COLOR_TAGS = [
  { id:"green",  label:"Green",  hex:"#16a34a" },
  { id:"blue",   label:"Blue",   hex:"#2563eb" },
  { id:"red",    label:"Red",    hex:"#dc2626" },
  { id:"orange", label:"Orange", hex:"#ea580c" },
  { id:"purple", label:"Purple", hex:"#7c3aed" },
];
const COLOR_HEX = Object.fromEntries(COLOR_TAGS.map(c => [c.id, c.hex]));

// ── Date grouping ──────────────────────────────────────────────────────────────
const getGroupKey = (isoDate) => {
  const d = new Date(isoDate);
  const now = new Date();
  const todayStr = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const jobDay   = new Date(d.getFullYear(),  d.getMonth(),  d.getDate()).getTime();
  const diff = Math.round((todayStr - jobDay) / 86400000);
  if (diff === 0)  return "__today__";
  if (diff === 1)  return "__yesterday__";
  return d.toLocaleDateString("en-IN", { day:"2-digit", month:"2-digit", year:"numeric" });
};
const GROUP_LABEL = { "__today__":"Today", "__yesterday__":"Yesterday" };

const groupJobs = (jobs) => {
  const order = [];
  const map   = {};
  jobs.forEach(job => {
    const key = getGroupKey(job.created_at);
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(job);
  });
  return order.map(key => ({ key, label: GROUP_LABEL[key] || key, jobs: map[key] }));
};

// ─────────────────────────────────────────────────────────────────────────────
export default function SCJobsList() {
  const navigate = useNavigate();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [jobs,         setJobs]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page,         setPage]         = useState(1);

  // ── Date filters ──────────────────────────────────────────────────────────
  const [dateFilter,  setDateFilter]  = useState("all");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [showRange,   setShowRange]   = useState(false);

  // ── Color filter ──────────────────────────────────────────────────────────
  const [colorFilter, setColorFilter] = useState("all"); // "all"|"green"|"blue"|"red"|"orange"|"purple"

  // ── Selection ─────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState(new Set());
  const allSelected = jobs.length > 0 && jobs.every(j => selected.has(j.id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(jobs.map(j => j.id)));
  const toggleSelect = id => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const [bulkStatusOpen,  setBulkStatusOpen]  = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState("");
  const [bulkDeleteOpen,  setBulkDeleteOpen]  = useState(false);
  const [bulkActing,      setBulkActing]      = useState(false);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [notesJob,    setNotesJob]    = useState(null);
  const [noteText,    setNoteText]    = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  // ── Color picker ──────────────────────────────────────────────────────────
  const [colorPickerJobId, setColorPickerJobId] = useState(null);
  const colorPickerRef = useRef(null);

  useEffect(() => {
    const close = e => { if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) setColorPickerJobId(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // ── Quick status update (inline on card) ──────────────────────────────────
  const [quickStatusJobId, setQuickStatusJobId] = useState(null);
  const [quickStatusSaving, setQuickStatusSaving] = useState(null); // jobId being saved
  const quickStatusRef = useRef(null);

  useEffect(() => {
    const close = e => {
      if (quickStatusRef.current && !quickStatusRef.current.contains(e.target))
        setQuickStatusJobId(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleQuickStatus = async (e, job, newStatus) => {
    e.preventDefault();
    e.stopPropagation();
    if (newStatus === job.current_status) { setQuickStatusJobId(null); return; }
    setQuickStatusSaving(job.id);
    setQuickStatusJobId(null);
    try {
      const r = await api.patch(`${API}/sc/jobs/${job.id}/status`, { status: newStatus, notes: "" });
      setJobs(prev => prev.map(j => j.id === job.id ? r.data : j));
      toast.success(`${job.sc_no} → ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
    finally { setQuickStatusSaving(null); }
  };

  // ── Compute date params ────────────────────────────────────────────────────
  const getDateParams = useCallback(() => {
    if (dateFilter === "today")     return { date_from: today(),           date_to: today() };
    if (dateFilter === "yesterday") return { date_from: offsetDay(-1),     date_to: offsetDay(-1) };
    if (dateFilter === "last7")     return { date_from: offsetDay(-6),     date_to: today() };
    if (dateFilter === "range" && dateFrom && dateTo) return { date_from: dateFrom, date_to: dateTo };
    if (dateFilter === "month" && monthFilter) {
      const [y, m] = monthFilter.split("-").map(Number);
      return { month: m, year: y };
    }
    return {};
  }, [dateFilter, dateFrom, dateTo, monthFilter]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, ...getDateParams() };
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (colorFilter  !== "all") params.color  = colorFilter;
      const r = await api.get(`${API}/sc/jobs`, { params });
      setJobs(r.data.jobs || []);
      setTotal(r.data.total || 0);
    } catch { toast.error("Failed to load SC jobs"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, colorFilter, getDateParams]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => { setPage(1); }, [search, statusFilter, colorFilter, dateFilter, dateFrom, dateTo, monthFilter]);

  // ── Date filter buttons ────────────────────────────────────────────────────
  const setQuickFilter = (f) => {
    setDateFilter(f);
    setShowRange(false);
    setMonthFilter("");
  };

  // ── Bulk Status ────────────────────────────────────────────────────────────
  const handleBulkStatus = async () => {
    if (!bulkStatusValue) return;
    setBulkActing(true);
    try {
      const r = await api.patch(`${API}/sc/jobs/bulk/status`, { ids: [...selected], status: bulkStatusValue });
      toast.success(`Updated ${r.data.updated} job${r.data.updated !== 1 ? "s" : ""}`);
      setBulkStatusOpen(false); setBulkStatusValue(""); setSelected(new Set()); fetchJobs();
    } catch { toast.error("Failed to update status"); }
    finally { setBulkActing(false); }
  };

  // ── Bulk Delete ────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    setBulkActing(true);
    try {
      const ids = [...selected];
      await api.delete(`${API}/sc/jobs/bulk`, { data: { ids } });
      toast.success(`${ids.length} job${ids.length !== 1 ? "s" : ""} deleted`);
      setBulkDeleteOpen(false); setSelected(new Set()); fetchJobs();
    } catch { toast.error("Failed to delete"); }
    finally { setBulkActing(false); }
  };

  // ── Export Excel ───────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    const toExport = selected.size > 0 ? jobs.filter(j => selected.has(j.id)) : jobs;
    if (!toExport.length) { toast.error("No jobs to export"); return; }
    const rows = toExport.map(job => ({
      "SC No":           job.sc_no,
      "Status":          job.current_status,
      "Color Tag":       job.color || "",
      "Customer Name":   job.customer?.name  || "",
      "Phone":           job.customer?.phone || "",
      "Alt Phone":       job.customer?.alt_phone || "",
      "Address":         job.customer?.address || "",
      "Date":            fmtDate(job.created_at),
      "No. of Chargers": (job.chargers || []).length,
      "Charger Types":   (job.chargers || []).map(c => c.charger_type || "").filter(Boolean).join("; "),
      "Charger Models":  (job.chargers || []).map(c => c.charger_spec || "").filter(Boolean).join("; "),
      "Serial Numbers":  (job.chargers || []).map(c => c.serial_number || "").filter(Boolean).join("; "),
      "Warranty Status": (job.chargers || []).map(c => c.warranty_status || "").filter(Boolean).join("; "),
      "Total (₹)":       job.payment?.amount      || 0,
      "Paid (₹)":        job.payment?.paid_amount || 0,
      "Due (₹)":         job.payment?.due_amount  || 0,
      "Payment Status":  job.payment?.payment_status || "",
      "Fault":           job.specs?.fault_description || "",
      "Internal Notes":  (job.internal_notes || []).map(n => n.text).join(" | "),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SC Jobs");
    XLSX.writeFile(wb, `SC_Jobs_${new Date().toISOString().slice(0,10)}.xlsx`);
    toast.success(`Exported ${toExport.length} jobs`);
  };

  // ── Notes ─────────────────────────────────────────────────────────────────
  const openNotes = (e, job) => { e.preventDefault(); e.stopPropagation(); setNotesJob(job); setNoteText(""); };
  const handleAddNote = async () => {
    if (!noteText.trim() || !notesJob) return;
    setNotesSaving(true);
    try {
      const r = await api.post(`${API}/sc/jobs/${notesJob.id}/notes`, { text: noteText.trim() });
      setNotesJob(r.data); setNoteText("");
      setJobs(prev => prev.map(j => j.id === r.data.id ? r.data : j));
      toast.success("Note saved");
    } catch { toast.error("Failed to save note"); }
    finally { setNotesSaving(false); }
  };
  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`${API}/sc/jobs/${notesJob.id}/notes/${noteId}`);
      const updated = { ...notesJob, internal_notes: (notesJob.internal_notes || []).filter(n => n.id !== noteId) };
      setNotesJob(updated); setJobs(prev => prev.map(j => j.id === updated.id ? updated : j));
    } catch { toast.error("Failed to delete note"); }
  };

  // ── Color tag ─────────────────────────────────────────────────────────────
  const handleColorChange = async (jobId, color) => {
    setColorPickerJobId(null);
    try {
      await api.patch(`${API}/sc/jobs/${jobId}/color`, { color });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, color } : j));
    } catch { toast.error("Failed to set color"); }
  };

  // ── Customer request match notifications ──────────────────────────────────
  const [matches,        setMatches]        = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesDismissed, setMatchesDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("sc_matches_dismissed") || "[]"); }
    catch { return []; }
  });

  const fetchMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const r = await api.get(`${API}/sc/request-matches`);
      setMatches(r.data.matches || []);
    } catch { /* silent — notifications are non-critical */ }
    finally { setMatchesLoading(false); }
  }, []);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const dismissMatch = (key) => {
    const next = [...matchesDismissed, key];
    setMatchesDismissed(next);
    try { sessionStorage.setItem("sc_matches_dismissed", JSON.stringify(next)); } catch {}
  };

  const visibleMatches = matches.filter(m => !matchesDismissed.includes(`${m.request.job_no}:${m.sc_job.sc_no}`));

  const MATCH_LABELS = {
    phone:  { label:"Phone Match",         icon:"📞", cls:"border-blue-200 bg-blue-50",    badge:"bg-blue-100 text-blue-800 border-blue-200" },
    serial: { label:"Serial No. Match",    icon:"🔢", cls:"border-amber-200 bg-amber-50",  badge:"bg-amber-100 text-amber-800 border-amber-200" },
    both:   { label:"Phone + Serial Match",icon:"✅", cls:"border-green-200 bg-green-50",  badge:"bg-green-100 text-green-800 border-green-200" },
  };

  const SC_STATUSES = LEGACY_PIPELINE.map(s => s.status);
  const groups = groupJobs(jobs);

  const DATE_BTNS = [
    { id:"all",       label:"All" },
    { id:"today",     label:"Today" },
    { id:"yesterday", label:"Yesterday" },
    { id:"last7",     label:"Last 7 Days" },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-green-600 flex items-center justify-center flex-shrink-0">
            <Wrench size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
              Service Centre Jobs
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Chargers received at service centre · {total} total</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={handleExportExcel} data-testid="sc-export-btn">
            <Download size={13} /> Export Excel
          </Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white text-sm h-9"
            onClick={() => navigate("/sc/new")} data-testid="sc-new-job-btn">
            <Plus size={15} className="mr-1.5" /> New SC Job
          </Button>
        </div>
      </div>

      {/* ── Customer Request Match Notifications ────────────────────────── */}
      {visibleMatches.length > 0 && (
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">
              🔔 Customer Requests Matching SC Jobs ({visibleMatches.length})
            </span>
            <button onClick={() => { const keys = visibleMatches.map(m => `${m.request.job_no}:${m.sc_job.sc_no}`); const next = [...matchesDismissed, ...keys]; setMatchesDismissed(next); try { sessionStorage.setItem("sc_matches_dismissed", JSON.stringify(next)); } catch {} }}
              className="text-xs text-slate-400 hover:text-slate-600 underline ml-auto">
              Dismiss all
            </button>
          </div>
          {visibleMatches.map(m => {
            const ml   = MATCH_LABELS[m.matched_on] || MATCH_LABELS.phone;
            const key  = `${m.request.job_no}:${m.sc_job.sc_no}`;
            return (
              <div key={key} className={`flex items-start gap-3 p-3 rounded-2xl border ${ml.cls}`}>
                {/* Match type badge */}
                <div className="flex-shrink-0 pt-0.5">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${ml.badge}`}>
                    <span>{ml.icon}</span> {ml.label}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Customer request{" "}
                    <span className="font-bold font-mono">{m.request.job_no}</span>
                    {m.request.customer_name && <> from <span className="font-semibold">{m.request.customer_name}</span></>}
                    {m.request.phone && <> (📞 {m.request.phone})</>}
                    {" "}matches SC job{" "}
                    <button
                      onClick={() => navigate(`/sc/${m.sc_job.id}`)}
                      className="font-bold font-mono underline hover:no-underline transition-all cursor-pointer">
                      {m.sc_job.sc_no}
                    </button>
                    {m.sc_job.customer_name && <> ({m.sc_job.customer_name})</>}
                    {" — "}
                    <span className="italic">
                      {m.matched_on === "both"   && "matched on both phone number and serial number"}
                      {m.matched_on === "phone"  && "matched on phone number"}
                      {m.matched_on === "serial" && "matched on charger serial number"}
                    </span>
                  </p>
                </div>

                {/* Dismiss */}
                <button onClick={() => dismissMatch(key)}
                  className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-700 rounded-lg transition-colors"
                  title="Dismiss">
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Date Filter Tabs ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200/70 p-4 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={14} className="text-slate-400 flex-shrink-0" />
          {/* Quick tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {DATE_BTNS.map(btn => (
              <button key={btn.id} onClick={() => setQuickFilter(btn.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                  dateFilter === btn.id
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-green-300 hover:text-green-700"
                }`}>
                {btn.label}
              </button>
            ))}
            {/* Range toggle */}
            <button
              onClick={() => { setShowRange(r => !r); setDateFilter("range"); setMonthFilter(""); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 ${
                dateFilter === "range"
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-green-300 hover:text-green-700"
              }`}>
              <Filter size={10} /> Custom Range
            </button>
          </div>

          <div className="w-px h-5 bg-slate-200 hidden sm:block" />

          {/* Month dropdown */}
            <Select
            value={monthFilter}
            onValueChange={v => { setMonthFilter(v === "__all__" ? "" : v); setDateFilter(v === "__all__" ? "all" : "month"); setShowRange(false); }}>
            <SelectTrigger className="h-8 text-xs w-44 border-slate-200">
              <SelectValue placeholder="📅 Filter by Month" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="__all__">All Months</SelectItem>
              {MONTH_OPTIONS.map(opt => (
                <SelectItem key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Refresh */}
          <Button variant="ghost" size="icon" className="h-8 w-8 ml-auto flex-shrink-0" onClick={fetchJobs}>
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>

        {/* Custom Date Range inputs */}
        {(showRange || dateFilter === "range") && (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">From</Label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs h-8 focus:outline-none focus:border-green-400" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">To</Label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs h-8 focus:outline-none focus:border-green-400" />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); setDateFilter("all"); setShowRange(false); }}
                className="text-xs text-slate-400 hover:text-rose-500 transition-colors">
                <X size={12} /> Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Color Filter Row ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-xs font-medium text-slate-500">Color:</span>
        {/* All */}
        <button
          onClick={() => setColorFilter("all")}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
            colorFilter === "all"
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
          }`}>
          All
        </button>
        {/* Color buttons */}
        {COLOR_TAGS.map(ct => (
          <button key={ct.id} onClick={() => setColorFilter(colorFilter === ct.id ? "all" : ct.id)}
            title={`Filter: ${ct.label}`}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
              colorFilter === ct.id
                ? "text-white border-transparent shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            style={colorFilter === ct.id ? { background: ct.hex, borderColor: ct.hex } : {}}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ct.hex }} />
            {ct.label}
          </button>
        ))}
        {colorFilter !== "all" && (
          <button onClick={() => setColorFilter("all")} className="text-xs text-slate-400 hover:text-rose-500 flex items-center gap-1 transition-colors">
            <X size={11} /> Clear
          </button>
        )}
      </div>

      {/* ── Search + Status Filter ──────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by job no, customer, phone, alt phone, serial no…"
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm" data-testid="sc-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 text-sm w-full sm:w-48">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {SC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* ── Bulk Toolbar ────────────────────────────────────────────────── */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-2xl px-4 py-2.5 mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CheckSquare size={15} className="text-green-600" />
            <span className="text-sm font-semibold text-green-800">{selected.size} selected</span>
            <button onClick={() => setSelected(new Set())} className="text-xs text-green-600 hover:text-green-800 underline ml-1">Clear</button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setBulkStatusOpen(true)} data-testid="bulk-status-btn">
              <CheckCircle size={12} className="mr-1" /> Update Status
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-slate-700 border-slate-200 hover:bg-slate-50"
              onClick={() => {
                const ids = [...selected].join(",");
                window.open(`/sc-bulk-print?jobs=${ids}`, "_blank");
              }} data-testid="bulk-print-btn">
              <Printer size={12} className="mr-1" /> Print Cards
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-btn">
              <Trash2 size={12} className="mr-1" /> Delete
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleExportExcel}>
              <Download size={12} className="mr-1" /> Export Excel
            </Button>
          </div>
        </div>
      )}

      {/* ── Select All row ───────────────────────────────────────────────── */}
      {jobs.length > 0 && !loading && (
        <div className="flex items-center gap-2 mb-3 px-1">
          <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} className="border-slate-300" data-testid="sc-select-all" />
          <span className="text-xs text-slate-500 select-none cursor-pointer" onClick={toggleSelectAll}>
            {allSelected ? "Deselect all" : `Select all ${jobs.length} jobs`}
          </span>
        </div>
      )}

      {/* ── Job List ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-36 rounded-2xl" />)}</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Wrench size={24} className="text-slate-400" />
          </div>
          <p className="text-slate-500 font-medium">No jobs found</p>
          <p className="text-sm text-slate-400 mt-1">Try adjusting the filters or create a new SC job</p>
          <Button className="mt-4 bg-green-600 hover:bg-green-700 text-white text-sm h-9" onClick={() => navigate("/sc/new")}>
            <Plus size={14} className="mr-1.5" /> New SC Job
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(group => (
            <div key={group.key}>
              {/* ── Date Group Header ─────────────────────────────────── */}
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  group.key === "__today__"
                    ? "bg-green-600 text-white"
                    : group.key === "__yesterday__"
                    ? "bg-slate-700 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}>
                  {group.label}
                </span>
                <span className="text-xs text-slate-400">{group.jobs.length} job{group.jobs.length !== 1 ? "s" : ""}</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              {/* ── Cards ─────────────────────────────────────────────── */}
              <div className="space-y-2.5">
                {group.jobs.map(job => {
                  const isSelected  = selected.has(job.id);
                  const isToday     = group.key === "__today__";
                  const notesCount  = (job.internal_notes || []).length;
                  const hasNotes    = notesCount > 0;
                  const chargers    = job.chargers || [];
                  const hasPayment  = job.payment?.amount > 0;
                  const jobColor    = job.color || "";
                  const hasCourier  = !!job.courier_details?.awb_number;

                  return (
                    <div key={job.id}
                      className={`relative bg-white rounded-2xl border transition-all overflow-visible ${
                        isSelected
                          ? "border-green-400 shadow-md ring-1 ring-green-200"
                          : isToday
                          ? "border-green-200 shadow-sm"
                          : "border-slate-200/70 hover:border-green-200 hover:shadow-sm"
                      }`}
                      data-testid={`sc-job-row-${job.sc_no}`}>

                      {/* Today marker — left green strip */}
                      {isToday && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-green-500" />
                      )}

                      <div className={`p-4 ${isToday ? "pl-5" : ""}`}>
                        <div className="flex items-start gap-3">
                          {/* Checkbox */}
                          <div className="pt-0.5 flex-shrink-0" onClick={e => { e.stopPropagation(); toggleSelect(job.id); }}>
                            <Checkbox checked={isSelected} className="border-slate-300" />
                          </div>

                          {/* Card Body */}
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/sc/${job.id}`)}>
                            {/* Row 1: SC No + Status + Today badge + Date */}
                            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold font-mono text-slate-900">{job.sc_no}</span>
                                {/* Quick status update badge */}
                                <div className="relative" ref={quickStatusJobId === job.id ? quickStatusRef : null}
                                  onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={e => { e.stopPropagation(); setQuickStatusJobId(prev => prev === job.id ? null : job.id); }}
                                    className="flex items-center gap-1 focus:outline-none group"
                                    title="Tap to change status"
                                    data-testid={`sc-quick-status-${job.sc_no}`}>
                                    {quickStatusSaving === job.id
                                      ? <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                          <Loader2 size={10} className="animate-spin" /> Saving…
                                        </span>
                                      : <>
                                          <StatusBadge status={job.current_status} />
                                          <ChevronDown size={11} className="text-slate-400 group-hover:text-slate-600 -ml-1 transition-colors" />
                                        </>}
                                  </button>

                                  {/* Dropdown */}
                                  {quickStatusJobId === job.id && (
                                    <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[200px]">
                                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 pt-2.5 pb-1">
                                        Update Status
                                      </p>
                                      {LEGACY_PIPELINE.filter((s, i, arr) => arr.findIndex(x => x.status === s.status) === i).map(step => {
                                        const isCurrent = step.status === job.current_status;
                                        return (
                                          <button
                                            key={step.status}
                                            onClick={e => handleQuickStatus(e, job, step.status)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors ${
                                              isCurrent
                                                ? "bg-green-50 text-green-800 font-semibold"
                                                : "text-slate-700 hover:bg-slate-50"
                                            }`}>
                                            <span className="text-sm">{step.emoji}</span>
                                            <span>{step.status}</span>
                                            {isCurrent && <span className="ml-auto text-[9px] bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full font-bold">Current</span>}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                {isToday && (
                                  <span className="text-[10px] font-bold text-green-700 bg-green-100 border border-green-200 px-2 py-0.5 rounded-full">
                                    TODAY
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock size={9} /> {fmtDate(job.created_at)}
                              </span>
                            </div>

                            {/* Customer */}
                            <p className="text-sm font-semibold text-slate-800">{job.customer?.name}</p>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              <span className="flex items-center gap-1 text-xs text-slate-500"><Phone size={9} /> {job.customer?.phone}</span>
                              {job.customer?.alt_phone && <span className="text-xs text-slate-400">Alt: {job.customer.alt_phone}</span>}
                              {job.customer?.address && (
                                <span className="flex items-center gap-1 text-xs text-slate-400"><MapPin size={9} /> {job.customer.address}</span>
                              )}
                            </div>

                            {/* Chargers — full detail table */}
                            {chargers.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                                  {chargers.length} Charger{chargers.length !== 1 ? "s" : ""}
                                </p>
                                <div className="overflow-x-auto rounded-xl border border-slate-100">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-slate-50/80 border-b border-slate-100">
                                        {["#","Type","Model","Serial No.","Body Type"].map(h => (
                                          <th key={h} className="text-left font-semibold text-slate-400 uppercase tracking-wide px-2.5 py-2 whitespace-nowrap">{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {chargers.map((c, ci) => (
                                        <tr key={ci} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40">
                                          <td className="px-2.5 py-2">
                                            <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold inline-flex items-center justify-center">{ci + 1}</span>
                                          </td>
                                          <td className="px-2.5 py-2 whitespace-nowrap">
                                            <span className="mr-1">{CHARGER_ICONS[c.charger_type] || "⚡"}</span>
                                            <span className="text-slate-600 font-medium">{c.charger_type || "—"}</span>
                                          </td>
                                          <td className="px-2.5 py-2">
                                            <span className="font-mono font-bold text-green-700 whitespace-nowrap">{c.charger_spec || "—"}</span>
                                          </td>
                                          <td className="px-2.5 py-2">
                                            <span className="font-mono text-slate-600 whitespace-nowrap">{c.serial_number || "—"}</span>
                                          </td>
                                          <td className="px-2.5 py-2 whitespace-nowrap">
                                            {c.body_type ? (
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${c.body_type === "Metal Body" ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                                                {c.body_type}
                                              </span>
                                            ) : <span className="text-slate-400">—</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Payment */}
                            {hasPayment && (
                              <div className="mt-2 flex items-center gap-3 flex-wrap">
                                <span className={`text-xs font-semibold ${PAY_STATUS_COLORS[job.payment.payment_status] || "text-slate-600"}`}>{job.payment.payment_status}</span>
                                <span className="text-xs text-slate-500 tabular-nums">Total ₹{(job.payment.amount||0).toLocaleString("en-IN")}</span>
                                <span className="text-xs text-emerald-600 tabular-nums">Paid ₹{(job.payment.paid_amount||0).toLocaleString("en-IN")}</span>
                                {(job.payment.due_amount||0) > 0 && (
                                  <span className="text-xs font-semibold text-rose-600 tabular-nums">Due ₹{(job.payment.due_amount).toLocaleString("en-IN")}</span>
                                )}
                                <span className="text-xs text-slate-400">{job.payment.method}</span>
                              </div>
                            )}
                          </div>

                          {/* Right actions column */}
                          <div className="flex flex-col items-center gap-2 flex-shrink-0 ml-1">
                            {/* Color dot */}
                            <div className="relative" ref={colorPickerJobId === job.id ? colorPickerRef : null}>
                              <button
                                onClick={e => { e.stopPropagation(); setColorPickerJobId(prev => prev === job.id ? null : job.id); }}
                                title="Set color tag"
                                className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-125 flex-shrink-0"
                                style={{
                                  background: jobColor ? COLOR_HEX[jobColor] : "transparent",
                                  borderColor: jobColor ? COLOR_HEX[jobColor] : "#cbd5e1",
                                }}
                              />
                              {/* Color picker popover */}
                              {colorPickerJobId === job.id && (
                                <div className="absolute right-0 top-7 z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-2.5 flex gap-1.5"
                                  onClick={e => e.stopPropagation()}>
                                  {COLOR_TAGS.map(ct => (
                                    <button key={ct.id} title={ct.label}
                                      onClick={() => handleColorChange(job.id, jobColor === ct.id ? "" : ct.id)}
                                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${jobColor === ct.id ? "border-slate-900 scale-110" : "border-transparent"}`}
                                      style={{ background: ct.hex }}
                                    />
                                  ))}
                                  {jobColor && (
                                    <button title="Remove color" onClick={() => handleColorChange(job.id, "")}
                                      className="w-6 h-6 rounded-full border-2 border-slate-200 bg-slate-50 flex items-center justify-center hover:border-rose-300 transition-colors">
                                      <X size={9} className="text-slate-400" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Courier badge */}
                            {hasCourier && (
                              <div title={`Courier: ${job.courier_details.courier_name || ""} | AWB: ${job.courier_details.awb_number}`}
                                className="p-1.5 rounded-lg bg-blue-50 text-blue-600">
                                <Truck size={13} />
                              </div>
                            )}

                            {/* Notes icon */}
                            <button onClick={e => openNotes(e, job)} title={hasNotes ? `${notesCount} note${notesCount !== 1 ? "s" : ""}` : "Add note"}
                              className={`relative p-1.5 rounded-lg transition-colors ${hasNotes ? "text-green-600 bg-green-50 hover:bg-green-100" : "text-slate-400 hover:text-green-600 hover:bg-green-50"}`}
                              data-testid={`sc-notes-btn-${job.sc_no}`}>
                              <StickyNote size={14} />
                              {hasNotes && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                  {notesCount > 9 ? "9+" : notesCount}
                                </span>
                              )}
                            </button>

                            {/* Chevron */}
                            <button onClick={() => navigate(`/sc/${job.id}`)} className="p-1 text-slate-300 hover:text-green-500 transition-colors">
                              <ChevronRight size={15} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {total > 50 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-slate-500">Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={jobs.length < 50} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* ═══════════════════════ NOTES PANEL ══════════════════════════════ */}
      <Sheet open={!!notesJob} onOpenChange={open => { if (!open) { setNotesJob(null); setNoteText(""); } }}>
        <SheetContent side="right" className="w-full sm:w-[420px] flex flex-col p-0">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <StickyNote size={15} className="text-green-600" />
              <SheetTitle className="text-base" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Notes — {notesJob?.sc_no}
              </SheetTitle>
            </div>
            <SheetDescription className="text-xs text-slate-400">Internal only — not visible to customers.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {(notesJob?.internal_notes || []).length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <StickyNote size={28} className="text-slate-200 mb-3" />
                <p className="text-sm text-slate-400 font-medium">No notes yet</p>
              </div>
            ) : (
              [...(notesJob?.internal_notes || [])].reverse().map(note => (
                <div key={note.id} className="bg-slate-50 rounded-xl p-3 border border-slate-100 group">
                  <p className="text-sm text-slate-800 leading-relaxed">{note.text}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Clock size={9} /> {fmtDT(note.created_at)}</p>
                    <button onClick={() => handleDeleteNote(note.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-rose-500 rounded transition-all">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="px-5 py-4 border-t border-slate-100 bg-white">
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Add New Note</Label>
            <Textarea value={noteText} onChange={e => setNoteText(e.target.value)}
              placeholder="Type your note here…" className="text-sm resize-none mb-2" rows={3}
              onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleAddNote(); }} />
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white text-sm h-9"
              disabled={!noteText.trim() || notesSaving} onClick={handleAddNote} data-testid="sc-save-note-btn">
              {notesSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save Note
            </Button>
            <p className="text-[10px] text-slate-400 text-center mt-1.5">Ctrl + Enter to save</p>
          </div>
        </SheetContent>
      </Sheet>

      {/* ═══════════════════ BULK STATUS DIALOG ═══════════════════════════ */}
      <Dialog open={bulkStatusOpen} onOpenChange={setBulkStatusOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
              Update Status — {selected.size} Job{selected.size !== 1 ? "s" : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-medium">New Status</Label>
            <Select value={bulkStatusValue} onValueChange={setBulkStatusValue}>
              <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder="Select status…" /></SelectTrigger>
              <SelectContent>{LEGACY_PIPELINE.map(s => <SelectItem key={s.status} value={s.status}>{s.emoji} {s.status}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusOpen(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={!bulkStatusValue || bulkActing} onClick={handleBulkStatus}>
              {bulkActing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════ BULK DELETE DIALOG ═══════════════════════════ */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} SC Job{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleBulkDelete} disabled={bulkActing}>
              {bulkActing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
